import {ChangeEvent, MouseEvent, Dispatch, SetStateAction} from 'react';
import {History} from 'history';
import {Action} from 'redux-actions';
import {OnSelectCallbackParam} from 'react-daterange-picker';

import {OnStringSetState} from '@pluto/@types';
import {Sort} from '@pluto/table';
import {UserValue} from '@pluto/users';
import {NewFilters} from '@pluto/qc-preselect';
import {DateRangeState} from '@pluto/daterange-picker';
import {QCFilters, QCSpecificFilters} from '../QCFilters/QCFiltersType';
import {JiraIssue} from '../QCJobs/QCJobsDialogs/JiraMenu/JiraTypes';
import {
    PriorityRequestBody,
    AssignRequestBody,
    UnassignRequestBody,
    DueDateSettingRequestBody,
} from './Redux/QCManagerActions';

export interface QCContentItem {
    id: number;
    contentItemType: string;
    title?: string;
    partnerName?: string;
    seriesTitle?: string;
    seasonNumber?: number;
    number?: number;
    labPartner: string;
    displayTitle?: string;
    contentPartner: {
        id: number;
        partnerName: string;
    };
    mamId: string;
}

export interface Assignee {
    id: number;
    name?: string;
    email: string;
}

export interface QCJobItem {
    id: number;
    priority: number;
    assignedAt: string | null;
    assignee?: Assignee;
    assigneeName?: string;
    status?: string;
    contentItem: QCContentItem;
    contentType?: string;
    dueDate: string | null;
    startDate: string | null;
    seriesTitle?: string;
    finishDate: string | null;
    contentPartner: string;
    labPartner: string;
    issues: JiraIssue[];
    title?: string;
}

export interface QCJobItemModified extends QCJobItem {
    title?: string;
    season?: number;
    episode?: number;
    displayTitle?: string;
}

export interface QCJobsListPayload {
    rows: QCJobItemModified[];
    total: number;
}

export interface QCSummaryResponse {
    READY_FOR_QC: number;
    IN_QC: number;
    QC_FAILED: number;
    QC_PASSED: number;
    [key: string]: number | QCJobItemModified[];
}

export interface QCSummaryUpdatedAction {
    status: string;
    id: number;
}

export interface QCFiltersParams extends QCSpecificFilters, Sort {
    offset?: number;
    limit?: number;
    title?: string;
    dateStart?: string;
    dateEnd?: string;
    finishDateStart?: string;
    finishDateEnd?: string;
}

export type SelectChangeEventType = {
    name?: string;
    value: any;
};

export interface QCManagerState extends QCSummaryResponse {
    rows: QCJobItemModified[];
    total: number;
}

export enum FiltersKeys {
    Type = 'qcJobType',
    ContentPartnerId = 'contentPartnerId',
    ContentType = 'contentItemType',
    Assignee = 'qcJobAssigneeId',
    Status = 'qcJobStatus',
}

export enum Priorities {
    Highest = 1,
    High = 2,
    Medium = 3,
    Low = 4,
    Lowest = 5,
}

export enum PriorityLevels {
    Highest = '1 - Highest',
    High = '2 - High',
    Medium = '3 - Medium',
    Low = '4 - Low',
    Lowest = '5 - Lowest',
}

export interface JobPriorityProps {
    id?: number;
    priority?: number;
}

export type QCJobsPromise = Promise<QCJobsListPayload>;
export type AssignQCJob = (
    body: AssignRequestBody,
    params: QCFiltersParams
) => Action<QCJobsPromise>;
export type UnassignQCJob = (
    body: UnassignRequestBody,
    params: QCFiltersParams
) => Action<QCJobsPromise>;
export type SetQCJobsPriority = (
    body: PriorityRequestBody,
    params: QCFiltersParams
) => Action<QCJobsPromise>;
export type SetQcJobDueDate = (
    body: DueDateSettingRequestBody,
    params: QCFiltersParams
) => Action<QCJobsPromise>;
export type SetQCJobs = (params: QCFiltersParams) => Action<QCJobsPromise>;
export type OnInputChangeEvent = ChangeEvent<HTMLInputElement>;
export type SelectChangeEvent = ChangeEvent<SelectChangeEventType>;
export type HandleChangeSelectType = (e: SelectChangeEvent) => void;
export type HandleChangeInputType = (e: OnInputChangeEvent) => void;
export type HandlePreselectChange = (status: NewFilters) => void;
export type SetPriorityLevel = (value: Priorities) => void;
export type SetAssignedUser = (value: UserValue) => void;
export type SetPriorityModalOpen = (value: boolean) => void;
export type SetModalOpen = (value: boolean) => void;
export type SetMenuAnchorElem = (anchor: EventTarget | null) => void;
export type SetPopupAnchorElem = (anchor: EventTarget | null) => void;
export type SetPriorityDisplayedValue = (value: number) => void;
export type SetOpen = (value: boolean) => void;
export type ExportQCAction = (params: QCFiltersParams) => void;
export type ChangeJobPriorityAction = (
    body: JobPriorityProps[],
    params: QCFiltersParams
) => void;
export type GetQCJobsSummary = () => void;
export type onDatePickerChange = (daterange: OnSelectCallbackParam | null) => void;
export type SetCheckedItems = (items: number[]) => void;
export type SetCheckedJobsNames = (items: string[]) => void;
export type SetDates = Dispatch<SetStateAction<DateRangeState>>;

export interface QCManagerContainerProps {
    setQCJobs: SetQCJobs;
    assignQCJob: AssignQCJob;
    setQcJobDueDate: SetQcJobDueDate;
    unassignQCJob: UnassignQCJob;
    exportQCJobs: ExportQCAction;
    resetQCJobs: () => void;
    changeQCJobPriority: ChangeJobPriorityAction;
    getQCJobsSummary: GetQCJobsSummary;
    qcManager: QCManagerState;
    setNotification: any;
}

export interface FilterHandlers extends Sort {
    setFilters: (filters: QCSpecificFilters) => void;
    setPage: (value: number) => void;
    setQuery: (history: History, route: string, params: QCFilters) => void;
    history: History;
    setQCJobs: (params: QCFilters) => void;
    filters: QCSpecificFilters;
    rowsPerPage: number;
    title: string;
    route: string;
    setDueDates: (value: DateRangeState) => void;
    setFinishDates: (value: DateRangeState) => void | undefined;
    setStartDates?: (value: DateRangeState) => void | undefined;
    dueDates: DateRangeState;
    finishDates?: DateRangeState;
    startDates?: DateRangeState;
}

export interface HandleFiltersParams extends FilterHandlers {
    key: FiltersKeys;
}

export interface QueryArgs {
    setQCJobs: (params: QCFilters) => void;
    setQuery: (history: History, params: QCFilters) => void;
    history: History;
}

export interface AssignDialogProps {
    singleItemId?: number;
    checkedItems?: number[];
    data: QCJobItemModified[];
    open: boolean;
    handleAssignOpen?: () => void;
    handleAssignClose: () => void;
    handleUserAssign: () => void;
    handleSelectChangeAssign: (value: UserValue) => void;
    isAssigned: boolean;
    assignedUser: UserValue;
}

export interface HandleChangeCheckboxParams {
    checkedItems: number[];
    setCheckedItems: SetCheckedItems;
}

export interface QCManagerMenuProps {
    handleMenuOpen: (event: MouseEvent) => void;
    handleMenuChangePriority: () => void;
    handleMenuChangeAssignee: () => void;
    handleMenuClose: () => void;
    menuAnchorElem: any;
}

export interface AssignDialogContainerProps {
    data: QCJobItemModified[];
    id: number;
    assignee: UserValue;
    assignedUser: UserValue;
    setAssignedUser: (value: UserValue) => void;
    handleSelectChangeAssign: any;
    assignQCJob: AssignQCJob;
    unassignQCJob: UnassignQCJob;
    params: QCFilters;
}

export interface HandlePriorityChangeParams {
    priorityValue?: number;
    changeQCJobPriority: ChangeJobPriorityAction;
    setCheckedItems: SetCheckedItems;
    setPriorityDisplayedValue: SetPriorityDisplayedValue;
    setPopupAnchorElem: SetMenuAnchorElem;
    setOpen: SetOpen;
    jobId: number;
    params: QCFilters;
}

export interface RenderPriorityPopupProps {
    popupAnchorElem: Element;
    setPopupAnchorElem: SetPopupAnchorElem;
    setPriorityLevel: SetPriorityLevel;
    priorityLevel: Priorities;
    changeQCJobPriority: ChangeJobPriorityAction;
    params: QCFilters;
}

export interface HandlePriorityChange {
    checkedItems: number[];
    setPriorityModalOpen: SetPriorityModalOpen;
    priorityLevel: Priorities;
    changeQCJobPriority: ChangeJobPriorityAction;
    setQCJobs: SetQCJobs;
    params: QCFilters;
    setCheckedItems: SetCheckedItems;
}

export interface HandleUserAssign {
    checkedItems: number[];
    assignedUser: UserValue;
    setAssignedUser: OnStringSetState;
    setAssignModalOpen: (value: boolean) => void;
    params: QCFilters;
    assignQCJob: AssignQCJob;
    setCheckedItems: SetCheckedItems;
}

export interface HandleUserUnassign {
    checkedItems: number[];
    setAssignModalOpen: (value: boolean) => void;
    params: QCFilters;
    unassignQCJob: UnassignQCJob;
    setCheckedItems: SetCheckedItems;
}

export interface RenderAssignLink {
    setAssignedUser: (value: UserValue) => void;
    handleSelectChangeAssign: SetAssignedUser;
    assignQCJob: AssignQCJob;
    unassignQCJob: UnassignQCJob;
    item: QCJobItem;
    params: QCFilters;
    assignedUser: UserValue;
}

export interface HandleMultipleDueDateChangeParams {
    checkedItems: number[];
    params: QCFilters;
    setQcJobDueDate: SetQcJobDueDate;
    setCheckedItems: SetCheckedItems;
}
